import { JSX } from "react";
import Link from "next/link";
import { Trans } from "@lingui/macro";
import { BREAKPOINTS } from "src/breakpoints";
import { Content } from "src/atoms/Content";
import { Button } from "src/atoms/Button";
import { ResponsivePicture } from "src/atoms/ResponsivePicture";
import { Check } from "src/icons";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { useUsp } from "src/hooks/useUsp";
import { segment, SignEvent, SignLocation } from "src/tracking/segment";
import {
  Section,
  Grid,
  TitleGroup,
  Pill,
  Title,
  Subtitle,
  Media,
  Stats,
  Stat,
  StatValue,
  StatCaption,
  Checks,
  CheckItem,
  Actions,
  LoginLink,
} from "./UspValueBlock.styled";

const UspValueBlock = (): JSX.Element | null => {
  const { currentShopType } = useShopTypeContext();
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();

  const { data } = useUsp(currentShopType, isSessionLoading || isLoggedIn);
  const usp = data?.usp;

  if (isSessionLoading || isLoggedIn || !usp?.title) {
    return null;
  }

  const hasStats = usp.additional_details.length > 0;

  return (
    <Section data-testid="usp-value-block">
      <Content>
        <Grid>
          <TitleGroup>
            {usp.pill && <Pill>{usp.pill}</Pill>}
            <Title>{usp.title}</Title>
            {usp.subtitle && <Subtitle>{usp.subtitle}</Subtitle>}
          </TitleGroup>

          <Media hasStats={hasStats}>
            <ResponsivePicture
              mobile={{ src: usp.mobile_image, width: 328, height: 270 }}
              tablet={{ src: usp.web_image, width: 600, height: 450 }}
              desktop={{ src: usp.web_image, width: 600, height: 450 }}
              sizes={`(max-width: ${BREAKPOINTS.md}) 100vw, 600px`}
            />
            {hasStats && (
              <Stats>
                {usp.additional_details.map((detail, index) => (
                  <Stat key={index}>
                    <StatValue>{detail.value_1}</StatValue>
                    <StatCaption>{detail.value_2}</StatCaption>
                  </Stat>
                ))}
              </Stats>
            )}
          </Media>

          {usp.description.length > 0 && (
            <Checks>
              {usp.description.map((line, index) => (
                <CheckItem key={index}>
                  <Check width={24} height={24} aria-hidden="true" />
                  {line}
                </CheckItem>
              ))}
            </Checks>
          )}

          <Actions>
            <Link href="/register" prefetch={false} passHref legacyBehavior>
              <Button
                as="a"
                variant="primary"
                colorScheme="dark"
                fullWidth
                onClick={() =>
                  segment.signHandler(SignEvent.SIGN_UP_STARTED, {
                    location: SignLocation.USP_BLOCK,
                  })
                }
              >
                <Trans>Sign up</Trans>
              </Button>
            </Link>
            <Link href="/sign-in" prefetch={false} passHref legacyBehavior>
              <LoginLink
                onClick={() =>
                  segment.signHandler(SignEvent.SIGN_IN_STARTED, {
                    location: SignLocation.USP_BLOCK,
                  })
                }
              >
                <Trans>Login</Trans>
              </LoginLink>
            </Link>
          </Actions>
        </Grid>
      </Content>
    </Section>
  );
};

export default UspValueBlock;
